import { useEffect, useState } from 'react'
import { t, anzahlwort } from './sprache'
import aufnahmen from './hilfebilder.json'
import { ANLEITUNGEN } from './Anleitungen'
import type { Schritt } from './Anleitungen'

/**
 * Die Hilfe: Schritt für Schritt durch die Wege, die man selten geht (R304).
 *
 * **Warum Bilder und nicht nur Text.** Der Import ist ein Weg über vier
 * Bildschirme, und wer ihn zum ersten Mal geht, sucht nicht die Erklärung,
 * sondern den Knopf. Ein Satz beschreibt, wo er ist; ein Bild zeigt es.
 *
 * **Warum die Markierung über dem Bild liegt und nicht darin.** Ein Rahmen,
 * der in die PNG-Datei gemalt wäre, müsste bei jeder Änderung der Oberfläche
 * neu gemalt werden — und wer das vergisst, hat eine Anleitung, die auf die
 * falsche Stelle zeigt, ohne dass es jemand sieht. Hier ist das Bild nur ein
 * Bild: Die Aufnahmen entstehen mit `web/hilfe/aufnahmen.cjs` neu, die
 * Markierungen stehen daneben in Prozent der Bildbreite und bleiben.
 *
 * **Warum ein eigener Bereich und kein Fragezeichen je Bildschirm.** Ein
 * Fragezeichen beantwortet die Frage „was ist das hier", die Anleitung die
 * Frage „wie komme ich von nichts zu Zahlen". Das sind zwei Fragen, und die
 * zweite braucht einen Ort, an dem alle Schritte nebeneinander stehen.
 */

/** Ein markierter Ausschnitt im Bild — in Prozent, damit er mitskaliert. */
type Markierung = {
  links: number
  oben: number
  breite: number
  hoehe: number
  /** Was dort zu tun ist. Steht als Fahne am Rahmen. */
  ruf?: string
}

/** Eine gemessene Aufnahme: Bild, Datum, Markierungen. */
type Aufnahmesatz = {
  bild: string
  datum: string
  markierungen: Markierung[]
}

const AUFNAHMEN = aufnahmen as Record<string, Aufnahmesatz>

export function Hilfe({
  unterpunkt,
  aufSprung,
}: {
  unterpunkt: string | null
  aufSprung: (bereich: string, unterpunkt?: string) => void
}) {
  const [stelle, setStelle] = useState(0)

  const anleitung = ANLEITUNGEN[unterpunkt ?? 'import'] ?? ANLEITUNGEN.import!
  const SCHRITTE_HIER = anleitung.schritte

  // Beim Wechsel der Anleitung wieder vorn anfangen: Der dritte Schritt der
  // einen hat mit dem dritten der anderen nichts zu tun.
  useEffect(() => setStelle(0), [unterpunkt])

  // Der Vorrat ist fest und nicht leer — die Notlösung steht trotzdem da,
  // weil eine Anleitung ohne Schritt kein Bildschirm sein darf, der abstürzt.
  const schritt = SCHRITTE_HIER[stelle] ?? SCHRITTE_HIER[0]!

  return (
    <section className="hilfe">
      <h1>{t(anleitung.titel)}</h1>
      <p className="erlaeuterung">{t('hilfe.anleitung.zweck')}</p>

      {/* Die Schrittleiste ist zugleich die Übersicht: Wer den dritten Schritt
          nachschlägt, will nicht zweimal weiterblättern. */}
      <ol className="hilfeschritte">
        {SCHRITTE_HIER.map((s, i) => (
          <li key={s.titel} className={i === stelle ? 'aktiv' : undefined}>
            {/* **`aria-current` gehört an den Knopf, nicht nur die Klasse an die
                Zeile.** Der aufgeschlagene Schritt war bisher allein farblich
                gekennzeichnet: Wer ihn noch einmal anklickt, löst nichts aus und
                erfährt nicht, warum. Für einen Screenreader war er von den
                übrigen gar nicht zu unterscheiden. */}
            <button
              type="button"
              aria-current={i === stelle ? 'step' : undefined}
              onClick={() => setStelle(i)}
            >
              <span className="nummer">{i + 1}</span>
              <span className="name">{s.titel}</span>
            </button>
          </li>
        ))}
      </ol>

      <div className="hilfeschritt">
        <p className="gedaempft">
          {t('hilfe.schritt', { nummer: String(stelle + 1), gesamt: anzahlwort(SCHRITTE_HIER.length) })}
        </p>
        <h2>{schritt.titel}</h2>
        <p>{schritt.text}</p>

        {schritt.ziel && (
          <p className="handreichung">
            <button
              type="button"
              className="ortsverweis"
              onClick={() => aufSprung(schritt.ziel!.bereich, schritt.ziel!.unterpunkt)}
            >
              {t('hilfe.dorthin')}
            </button>
          </p>
        )}

        <Aufnahme schritt={schritt} />

        <p className="handreichung">
          <button
            type="button"
            disabled={stelle === 0}
            onClick={() => setStelle((s) => Math.max(0, s - 1))}
          >
            {t('hilfe.zurueck')}
          </button>{' '}
          <button
            type="button"
            className="haupt"
            disabled={stelle === SCHRITTE_HIER.length - 1}
            onClick={() => setStelle((s) => Math.min(SCHRITTE_HIER.length - 1, s + 1))}
          >
            {t('hilfe.weiter')}
          </button>
        </p>
      </div>
    </section>
  )
}

/**
 * Das Bild mit seinen Markierungen.
 *
 * **Die Markierungen stehen in Prozent**, nicht in Bildpunkten: Das Bild
 * skaliert mit der Fensterbreite, und ein Rahmen in Pixeln säße bei jedem
 * anderen Fenster woanders.
 */
function Aufnahme({ schritt }: { schritt: Schritt }) {
  const [fehlt, setFehlt] = useState(false)
  const satz = AUFNAHMEN[schritt.id]

  if (!satz || fehlt) {
    return <p className="hinweis">{t('hilfe.bildfehlt')}</p>
  }

  return (
    <figure className="hilfebild">
      {/*
        **Die Bildfläche trägt die Markierungen, nicht das ganze `figure`.**
        Hingen sie am `figure`, rechnete der Prozentwert die Bildunterschrift
        mit — jeder Rahmen säße dann um deren Höhe zu tief, und zwar umso
        mehr, je weiter unten er steht.
      */}
      <span className="bildflaeche">
        <img src={satz.bild} alt={schritt.titel} onError={() => setFehlt(true)} />
        {satz.markierungen.map((m, i) => (
          <span
            key={i}
            className="markierung"
            style={{
              left: `${m.links}%`,
              top: `${m.oben}%`,
              width: `${m.breite}%`,
              height: `${m.hoehe}%`,
            }}
          >
            {m.ruf && <span className="fahne">{m.ruf}</span>}
          </span>
        ))}
      </span>
      <figcaption className="gedaempft">{t('hilfe.aufnahme', { datum: satz.datum })}</figcaption>
    </figure>
  )
}
